import { useRef, useEffect } from 'react';
import clsx from 'clsx';
import { Button } from '../Button';
import type { SectionStatus } from './SectionNav';

export interface SectionAccordionProps {
  id: string;
  title: string;
  eyebrow: string; // e.g. `SECTION_04 · LIFE_SAFETY`
  status: SectionStatus;
  progress?: { answered: number; total: number };
  expanded: boolean;
  onToggle: () => void;
  onSaveContinue?: () => void;
  onSaveDraft?: () => void;
  children: React.ReactNode;
  /** If true, the Save & continue button is disabled (e.g. validation errors pending). */
  saveDisabled?: boolean;
}

/**
 * Form-aware expand/collapse section. Wraps the existing `Card` aesthetic.
 *
 * The collapsed header shows the mono eyebrow, the section title, a progress
 * counter (N/M) and a state indicator. The expanded body renders arbitrary
 * children and a footer with "Save & continue" + "Save draft" actions.
 *
 * See docs/property/PRD.md §8.4 and Problem 2 proposal in the PRD.
 */
export function SectionAccordion({
  id,
  title,
  eyebrow,
  status,
  progress,
  expanded,
  onToggle,
  onSaveContinue,
  onSaveDraft,
  children,
  saveDisabled,
}: SectionAccordionProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLButtonElement>(null);

  // When a section opens, scroll its header into view so the user sees
  // the expanded content without confusion.
  useEffect(() => {
    if (expanded && headerRef.current) {
      const rect = headerRef.current.getBoundingClientRect();
      if (rect.top < 80 || rect.top > window.innerHeight * 0.6) {
        headerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [expanded]);

  const borderColor =
    status === 'error'
      ? 'border-[#b33a3a]/60'
      : expanded
        ? 'border-blue/40'
        : 'border-hairline';

  return (
    <section
      id={`section-${id}`}
      aria-labelledby={`section-${id}-header`}
      className={clsx(
        'bg-panel rounded-md shadow-panel border transition-colors',
        borderColor
      )}
    >
      <button
        ref={headerRef}
        id={`section-${id}-header`}
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={`section-${id}-body`}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="flex flex-col min-w-0">
          <span className="font-mono text-[0.6875rem] uppercase tracking-[0.125em] text-ink-subtle">
            &gt; {eyebrow}
          </span>
          <span className="font-display text-[1.0625rem] font-medium text-ink mt-1 truncate">
            {title}
          </span>
        </span>
        <span className="flex items-center gap-3 shrink-0">
          <HeaderStatus status={status} progress={progress} />
          <span
            aria-hidden="true"
            className={clsx(
              'inline-block font-mono text-ink-subtle transition-transform duration-200',
              expanded ? 'rotate-90' : ''
            )}
          >
            ›
          </span>
        </span>
      </button>
      {expanded ? (
        <div id={`section-${id}-body`} ref={bodyRef}>
          <div className="border-t border-hairline px-5 py-6">
            {children}
          </div>
          {(onSaveContinue || onSaveDraft) ? (
            <div className="border-t border-hairline px-5 py-4 flex flex-wrap gap-3 items-center">
              {onSaveContinue ? (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  onClick={onSaveContinue}
                  disabled={saveDisabled}
                >
                  Save &amp; continue
                </Button>
              ) : null}
              {onSaveDraft ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="md"
                  onClick={onSaveDraft}
                >
                  Save draft
                </Button>
              ) : null}
              {progress ? (
                <span className="ml-auto font-mono text-[0.6875rem] text-ink-subtle tabular-nums">
                  &gt; {progress.answered}_OF_{progress.total}_ANSWERED
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function HeaderStatus({
  status,
  progress,
}: {
  status: SectionStatus;
  progress?: { answered: number; total: number };
}) {
  if (status === 'complete') {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-blue-whisper/60 px-2.5 py-0.5"
      >
        <span aria-hidden="true" className="text-blue-deep font-mono text-[0.75rem]">
          ✓
        </span>
        <span className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-blue-deep">
          COMPLETE
        </span>
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-[#b33a3a]">
        &gt; HAS_ERRORS
      </span>
    );
  }
  if (status === 'partial' && progress) {
    return (
      <span className="font-mono text-[0.75rem] tabular-nums text-ink-subtle">
        {progress.answered} / {progress.total}
      </span>
    );
  }
  return (
    <span className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-ink-subtle">
      &gt; NOT_STARTED
    </span>
  );
}
